/* Sprint 5 — Recent Projects panel for the Dashboard.
 *
 * Self-fetches up to 10 most-recent Projects and renders them in a
 * compact table with per-row stage-progress indicators (DD → SPA → Risk)
 * so the analyst can see at a glance where each audit sits in the
 * 3-stage pipeline. Clicking a row opens that Project's Workspace.
 *
 * Replaces the Sprint-4 flat assessment list, which double-counted
 * (one row per Assessment instead of one row per Project).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatListingDateTime as formatDateTime } from '../../utils/formatListingDate';
import { getProject, listProjects } from '../../services/api';
import type { ProjectDetail, ProjectListItem } from '../../types/api';

type RowWithStages = ProjectListItem & {
  dd_status?: string;
  spa_status?: string;
  risk_status?: string;
  /* Sprint 5 fix — the per-stage deep-link buttons need the actual
     assessment_id for each stage, not a string-replaced derivation
     from the project's audit_id. DD and SPA assessments are issued
     from independent sequences (DD-2026-NNNN vs SPA-2026-MMMM), so
     `audit_id.replace('AUDIT-','SPA-')` produces a phantom ID like
     SPA-2026-0019 when no SPA-0019 actually exists. Carry the real
     IDs from the project detail payload. */
  dd_id?: string | null;
  spa_id?: string | null;
};

/* Client feedback 2026-06-19: the relative "2h ago" / "6h ago" format
   was ambiguous after long runs (was "12h ago" today or yesterday?).
   Switched to explicit date + time so the analyst can read an exact
   timestamp at a glance and correlate with worker logs.

   Sprint 5 client feedback 2026-07-07 (UI Issues #5) — routed through
   the shared formatListingDateTime utility (imported above) so every
   listing table shows the canonical "DD MMM YYYY, HH:mm" regardless
   of browser locale. Previously undefined-locale toLocaleDateString
   gave a US browser "Jul 19, 2026, 11:42" and a UK browser
   "19 Jul 2026, 11:42". */

function StageDot({ status }: { status?: string }): JSX.Element {
  /* Sprint 5 client feedback 2026-06-24 (FE Six issue 7) — Pipeline
     icon design standardised. Previous variant used a mix of glyphs
     (checkmark / ellipsis / open-circle / lock-emoji / cross) at
     wildly different visual weights. Switched to a single line-stroke
     SVG icon set drawn from the same Lucide-inspired vocabulary the
     sidebar uses, so completed/active/pending/locked/failed states
     are visually distinguishable but consistent. Same 20px circle
     container so column width and row height stay identical. */
  const s = status ?? 'pending';
  const colour = (() => {
    switch (s) {
      case 'complete': return { bg: 'var(--pass)', fg: '#fff' };
      case 'in_progress': return { bg: 'var(--info)', fg: '#fff' };
      case 'failed': return { bg: 'var(--fail)', fg: '#fff' };
      /* Sprint 5 client feedback 2026-07-08 (FE Ticket — Cancelled) —
         neutral tone matches "locked" so cancelled reads as
         terminal-but-non-error, not the red Failed state. */
      case 'cancelled': return { bg: 'var(--surface-alt)', fg: 'var(--muted)' };
      case 'locked': return { bg: 'var(--surface-alt)', fg: 'var(--muted)' };
      default: return { bg: 'var(--surface-alt)', fg: 'var(--muted)' };
    }
  })();
  const icon = (() => {
    switch (s) {
      case 'complete':
        return <polyline points="20 6 9 17 4 12" />;
      case 'in_progress':
        return (
          <>
            <circle cx="12" cy="12" r="2" />
            <circle cx="5" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </>
        );
      case 'failed':
        return (
          <>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </>
        );
      case 'cancelled':
        /* Sprint 5 client feedback 2026-07-08 (FE Ticket — Cancelled) —
           circle-slash icon reads as "user stopped" rather than the
           red X of a system failure. */
        return (
          <>
            <circle cx="12" cy="12" r="9" />
            <line x1="5" y1="5" x2="19" y2="19" />
          </>
        );
      case 'locked':
        return (
          <>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </>
        );
      default:
        // pending — open circle, hairline weight
        return <circle cx="12" cy="12" r="4" />;
    }
  })();
  return (
    <span
      aria-label={s}
      data-tip={s}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: colour.bg,
        color: colour.fg,
        border: s === 'pending' || s === 'locked' ? '1px solid var(--lines)' : 'none',
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {icon}
      </svg>
    </span>
  );
}

function OverallBadge({ status }: { status: string }): JSX.Element {
  const map: Record<string, string> = {
    in_progress: 'badge-info',
    complete: 'badge-pass',
    flagged: 'badge-watch',
    failed: 'badge-fail',
    // Sprint 5 client feedback 2026-07-08 (FE Ticket — Cancelled) —
    // Cancelled is a distinct terminal state, NOT a subset of Failed.
    // Neutral pill reflects the user-initiated non-error semantics;
    // was previously falling through to the badge-neutral default
    // WITHOUT the "Cancelled" label surfaced.
    cancelled: 'badge-neutral',
  };
  // Sprint 5 client feedback 2026-06-29 — Dashboard badge text only:
  // render "complete" as "Completed" per the client's preferred
  // wording. The status enum value is untouched (still "complete"
  // in the API / DB / filters); this is a display-only override.
  const labelMap: Record<string, string> = {
    complete: 'Completed',
    in_progress: 'In Progress',
    flagged: 'Flagged',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };
  return (
    <span className={`badge ${map[status] ?? 'badge-neutral'}`}>
      {labelMap[status] ?? status.replace('_', ' ')}
    </span>
  );
}

export function RecentProjectsTable(): JSX.Element | null {
  const navigate = useNavigate();
  const [rows, setRows] = useState<RowWithStages[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [hidden, setHidden] = useState(false);
  // Sprint 5 client feedback 2026-06-22 — Dashboard pass 2: add a
  // search + status + stage + analyst filter toolbar above the table
  // (per design audit doc item #4). Client-side filtering is fine for
  // the recent-10 view; if we ever expand to all projects we'd push
  // the filters down to the API.
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterAnalyst, setFilterAnalyst] = useState('');

  useEffect(() => {
    void load();

    async function load(): Promise<void> {
      try {
        const list = await listProjects({ limit: 10 });
        // Enrich each project with its stage statuses so the table
        // can show the DD→SPA→Risk progress dots without the user
        // having to click in. Concurrency capped to ≤10 calls.
        const enriched = await Promise.all(
          list.projects.map(async (p): Promise<RowWithStages> => {
            try {
              const detail: ProjectDetail = await getProject(p.audit_id);
              return {
                ...p,
                dd_status: detail.dd_assessment?.status,
                spa_status: detail.spa_assessment?.status,
                risk_status: detail.risk_assessment?.status,
                dd_id: detail.dd_assessment?.id ?? null,
                spa_id: detail.spa_assessment?.id ?? null,
              };
            } catch {
              return p;
            }
          }),
        );
        setRows(enriched);
        setLoaded(true);
      } catch {
        setHidden(true);
      }
    }
  }, []);

  if (hidden) return null;
  if (!loaded) {
    return (
      <div style={{ padding: 'var(--s5)', textAlign: 'center' }}>
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--s8) var(--s6)',
          textAlign: 'center',
          background: 'var(--surface)',
          border: '1px solid var(--lines)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>
          No projects yet
        </h3>
        <p className="t-muted" style={{ marginBottom: 'var(--s5)' }}>
          Start your first audit — every project covers DD → SPA → Risk
          Assessment under one ID.
        </p>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/projects/new')}
        >
          + New Project
        </button>
      </div>
    );
  }

  // Sprint 5 client feedback 2026-06-22 — derive the analyst list +
  // filter the rows in one pass. Filters are AND-combined.
  const analystOptions = Array.from(
    new Set(rows.map((r) => r.analyst_name).filter(Boolean) as string[]),
  ).sort();
  const visibleRows = rows.filter((r) => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      const hay = [
        r.project_name,
        r.audit_id,
        r.target_entity ?? '',
        r.analyst_name ?? '',
      ].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterStatus && r.overall_status !== filterStatus) return false;
    if (filterAnalyst && r.analyst_name !== filterAnalyst) return false;
    if (filterStage) {
      // "stage" filter = which stage is the project currently ACTIVE in
      // (the first non-complete stage, or "all_done" when everything's
      // complete).
      const stage =
        r.dd_status !== 'complete' ? 'dd'
        : r.spa_status !== 'complete' ? 'spa'
        : r.risk_status !== 'complete' ? 'risk'
        : 'all_done';
      if (stage !== filterStage) return false;
    }
    return true;
  });

  return (
    <section
      className="card"
      style={{
        padding: 0,
        overflow: 'hidden',
      }}
      aria-label="Recent projects"
    >
      <header
        style={{
          padding: 'var(--s4) var(--s5)',
          borderBottom: '1px solid var(--lines)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            margin: 0,
            fontSize: '1.05rem',
          }}
        >
          Recent Projects
        </h2>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate('/projects')}
        >
          View all →
        </button>
      </header>
      {/* Sprint 5 client feedback 2026-06-22 — Dashboard pass 2:
          filter toolbar (search + status + stage + analyst + export).
          Reuses the existing .filter-strip style from other list
          pages so the chrome stays consistent across the app. */}
      <div
        style={{
          padding: 'var(--s3) var(--s5)',
          borderBottom: '1px solid var(--lines)',
          background: 'var(--surface-alt, transparent)',
        }}
      >
        <div className="filter-strip" style={{ marginBottom: 0 }}>
          <div className="filter-search">
            <svg
              className="filter-search-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              className="filter-search-input"
              placeholder="Search projects, ID, target, analyst..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search projects"
            />
          </div>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label="Status filter"
          >
            <option value="">All Statuses</option>
            <option value="in_progress">In Progress</option>
            <option value="complete">Completed</option>
            <option value="flagged">Flagged</option>
            <option value="failed">Failed</option>
            {/* Sprint 5 client feedback 2026-07-08 (FE Ticket —
                Cancelled) — Cancelled is a distinct terminal state
                and must be filterable. Filter = Failed still
                excludes cancelled rows because the badge/label
                mapping keys off the exact enum value. */}
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            className="filter-select"
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            aria-label="Stage filter"
          >
            <option value="">All Stages</option>
            <option value="dd">Active in DD</option>
            <option value="spa">Active in SPA</option>
            <option value="risk">Active in Risk</option>
            <option value="all_done">Pipeline Complete</option>
          </select>
          <select
            className="filter-select"
            value={filterAnalyst}
            onChange={(e) => setFilterAnalyst(e.target.value)}
            aria-label="Analyst filter"
            disabled={analystOptions.length === 0}
          >
            <option value="">All Analysts</option>
            {analystOptions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setSearchTerm('');
              setFilterStatus('');
              setFilterStage('');
              setFilterAnalyst('');
            }}
            data-tip="Reset filters"
          >
            Clear
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => alert('Export not yet wired up.')}
            data-tip="Export project list"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export
          </button>
        </div>
      </div>
      <div className="table-wrap" style={{ overflowX: 'auto' }}>
        <table className="data-table" aria-label="Recent projects">
          <thead>
            <tr>
              <th>Project</th>
              {/* Sprint 5 client feedback 2026-06-24 (FE Six issue 4) —
                  Amount column removed. The deal_size_usd_m field is
                  rarely populated at the project-list level and the
                  column was reading as mostly em-dashes. Freed
                  horizontal space distributed across remaining columns
                  naturally via the table's auto-layout. */}
              <th>Analyst</th>
              <th>Pipeline</th>
              <th>Status</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 'var(--s5)', color: 'var(--muted)' }}>
                  No projects match the current filters.
                </td>
              </tr>
            )}
            {visibleRows.map((r) => (
              <tr
                key={r.audit_id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/projects/${r.audit_id}`)}
              >
                <td>
                  <span style={{ fontWeight: 600 }}>{r.project_name}</span>
                  <br />
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.68rem',
                      color: 'var(--muted)',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {r.audit_id}
                  </span>
                </td>
                <td className="t-xs" style={{ color: 'var(--text-sec)' }}>
                  {r.analyst_name || (
                    <span className="t-muted">—</span>
                  )}
                </td>
                <td>
                  <span
                    style={{
                      display: 'inline-flex',
                      gap: 6,
                      alignItems: 'center',
                    }}
                  >
                    <StageDot status={r.dd_status} />
                    <span style={{ color: 'var(--muted)', fontSize: '0.7rem' }}>→</span>
                    <StageDot status={r.spa_status} />
                    <span style={{ color: 'var(--muted)', fontSize: '0.7rem' }}>→</span>
                    <StageDot status={r.risk_status} />
                  </span>
                </td>
                <td>
                  <OverallBadge status={r.overall_status} />
                </td>
                <td className="t-muted" style={{ fontSize: '0.85rem' }}>
                  {formatDateTime(r.created_at)}
                </td>
                <td
                  style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      gap: 6,
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    {/* Eye icon = open Project Workspace */}
                    <button
                      type="button"
                      data-tip="Open project"
                      aria-label="Open project"
                      onClick={() => navigate(`/projects/${r.audit_id}`)}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--lines)',
                        borderRadius: '6px',
                        padding: '4px 6px',
                        cursor: 'pointer',
                        color: 'var(--text-sec)',
                        verticalAlign: 'middle',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                    {/* Per-stage deep-links — one icon button per
                        module, only enabled when that stage is
                        complete. Visually colour-codes by module
                        accent (DD amber / SPA emerald / Risk violet). */}
                    <StageIconButton
                    label="DD Report"
                    color="var(--dd-accent)"
                    enabled={r.dd_status === 'complete' && Boolean(r.dd_id)}
                    onClick={() => {
                      if (r.dd_id) navigate(`/reports/${r.dd_id}`);
                    }}
                  >
                    DD
                  </StageIconButton>
                  <StageIconButton
                    label="SPA Report"
                    color="var(--spa-accent)"
                    enabled={r.spa_status === 'complete' && Boolean(r.spa_id)}
                    onClick={() => {
                      if (r.spa_id) navigate(`/reports/${r.spa_id}`);
                    }}
                  >
                    SPA
                  </StageIconButton>
                  <StageIconButton
                    label="Draft Report"
                    color="var(--risk-accent)"
                    enabled={
                      r.risk_status === 'complete' ||
                      r.risk_status === 'degraded'
                    }
                    onClick={() =>
                      navigate(`/projects/${r.audit_id}/risk-report`)
                    }
                  >
                    DR
                  </StageIconButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* Small per-stage pill button shown in the action column. Dim + disabled
 * when the stage isn't complete; coloured pill when actionable. */
function StageIconButton({
  label,
  color,
  enabled,
  onClick,
  children,
}: {
  label: string;
  color: string;
  enabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  /* data-tip lives on a wrapper, not the button: a disabled control emits no
     mouse events, so the delegated tooltip would never fire — and the
     disabled state is exactly when "(not yet available)" needs explaining. */
  return (
    <span
      data-tip={enabled ? `Open ${label}` : `${label} not available yet`}
      style={{ display: 'inline-flex' }}
    >
    <button
      type="button"
      onClick={() => enabled && onClick()}
      disabled={!enabled}
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        background: enabled
          ? `color-mix(in srgb, ${color} 18%, transparent)`
          : 'var(--surface-alt)',
        color: enabled ? color : 'var(--muted)',
        border: `1px solid ${enabled ? color : 'var(--lines)'}`,
        borderRadius: '12px',
        fontFamily: 'var(--font-body, inherit)',
        fontSize: '0.7rem',
        fontWeight: 700,
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.6,
        letterSpacing: '0.04em',
        minWidth: 38,
        textAlign: 'center',
      }}
    >
      {children}
    </button>
    </span>
  );
}
